import type {ICreditQuote, ICreditRates} from "../models/interfaces/credits.interface.ts";
import {quoteAddLanguagesCredits, quoteCreateCredits} from "./creditsCost.ts";

// Fractional rates are outside the catalog contract; rates are whole credits per minute.
export type CreditBreakdownRowKind = "transcription" | "translation" | "dubbing" | "unselected-first-language";

export interface ICreditBreakdownRow {
  kind: CreditBreakdownRowKind;
  language: string | null;
  minutes: number;
  rate: number;
  credits: number;
}

export interface ICreditBreakdown {
  quote: ICreditQuote;
  rows: ICreditBreakdownRow[];
}

export type CreditBreakdownInput = {
  orderedTargetLanguages: readonly string[];
  dubbingScript: boolean;
  rates: ICreditRates;
} & (
  | {kind: "create"; fileDurationsSeconds: readonly number[]}
  | {kind: "add"; seconds: number; existingTranslationCount: number}
);

export const buildCreditBreakdown = (input: CreditBreakdownInput): ICreditBreakdown => {
  const {orderedTargetLanguages, dubbingScript, rates} = input;
  const targetCount = orderedTargetLanguages.length;
  const quote =
    input.kind === "create"
      ? quoteCreateCredits([...input.fileDurationsSeconds], targetCount, dubbingScript, rates)
      : quoteAddLanguagesCredits(
          input.seconds,
          targetCount,
          input.existingTranslationCount,
          dubbingScript,
          rates,
        );
  const rows: ICreditBreakdownRow[] = [];
  const addRow = (kind: CreditBreakdownRowKind, language: string | null, rate: number) => {
    rows.push({kind, language, minutes: quote.billedMinutes, rate, credits: quote.billedMinutes * rate});
  };

  if (input.kind === "create") {
    if (targetCount === 0) addRow("transcription", null, rates.transcriptionPerMinute);
    orderedTargetLanguages.forEach((language, index) => {
      addRow(
        "translation",
        language,
        index === 0 ? rates.firstTranslationPerMinute : rates.additionalTranslationPerMinute,
      );
    });
    if (dubbingScript) {
      if (targetCount === 0) {
        addRow("dubbing", null, rates.dubbingScriptPerMinutePerLanguage);
      } else {
        orderedTargetLanguages.forEach((language, index) => {
          addRow(
            "dubbing",
            language,
            index === 0
              ? rates.dubbingScriptPerMinutePerLanguage
              : rates.additionalTranslationPerMinute,
          );
        });
      }
    }
  } else {
    if (targetCount === 0 && input.existingTranslationCount === 0) {
      addRow("unselected-first-language", null, rates.transcriptionPerMinute);
    }
    orderedTargetLanguages.forEach((language, index) => {
      addRow(
        "translation",
        language,
        index === 0 && input.existingTranslationCount === 0
          ? rates.transcriptionPerMinute
          : rates.additionalTranslationPerMinute,
      );
    });
    if (dubbingScript) {
      orderedTargetLanguages.forEach((language) => {
        addRow("dubbing", language, rates.additionalTranslationPerMinute);
      });
    }
  }
  return {quote, rows};
};
